import React, {PureComponent} from 'react';
import {getCookie} from '../../helpers/cookie.helper';
import {
  Wrapper,
  CloseButton,
  Icon,
  Info,
  Title,
  Author,
  Description,
  ViewButton,
} from './AppBanner.style';

// How long after load the banner appears.
export const APP_BANNER_DELAY_MS = 10000;

const PLAY_STORE_ID = 'com.jjfoodservice.apollo';
const STORE = {
  android: {
    url: `https://play.google.com/store/apps/details?id=${PLAY_STORE_ID}`,
    description: 'Free - In Google Play',
  },
  ios: {
    url: 'https://apps.apple.com/app/id1540086548',
    description: 'Free - On the App Store',
  },
};

// Same cookie names and durations react-smartbanner used, so anyone who already
// closed (15 days) or tapped View (90 days) on the old banner keeps it hidden.
const CLOSED_COOKIE = 'smartbanner-closed';
const INSTALLED_COOKIE = 'smartbanner-installed';
const CLOSED_DAYS = 15;
const INSTALLED_DAYS = 90;

const setDismissCookie = (name, days) => {
  document.cookie = `${name}=true; path=/; max-age=${days *
    24 *
    60 *
    60}; SameSite=Lax`;
};

// Our own "get the app" banner: a fixed overlay over the top of the page, shown
// APP_BANNER_DELAY_MS after load, so it never moves page content (CLS). It renders
// nothing on the server or before the delay, so hydration markup always matches.
// On Android, View uses Chrome's own install prompt for the Play app when Chrome
// offers one (manifest.json related_applications), otherwise it opens the store.
export default class AppBanner extends PureComponent {
  state = {visible: false};

  componentDidMount() {
    if (getCookie(CLOSED_COOKIE) || getCookie(INSTALLED_COOKIE)) {
      return;
    }
    window.addEventListener(
      'beforeinstallprompt',
      this.handleBeforeInstallPrompt
    );
    this.timer = setTimeout(this.show, APP_BANNER_DELAY_MS);
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.timer);
    window.removeEventListener(
      'beforeinstallprompt',
      this.handleBeforeInstallPrompt
    );
  }

  handleBeforeInstallPrompt = event => {
    // Keep Chrome from showing its own mini-infobar as well; View shows the prompt.
    event.preventDefault();
    this.installPrompt = event;
  };

  show = () => {
    const {getInstalledRelatedApps} = window.navigator;
    if (typeof getInstalledRelatedApps !== 'function') {
      this.setState({visible: true});
      return;
    }
    // Hide when the app is already installed (Chrome on Android, where the app
    // declares this site; resolves to [] otherwise).
    getInstalledRelatedApps
      .call(window.navigator)
      .then(apps => !this.unmounted && this.setState({visible: !apps.length}))
      .catch(() => !this.unmounted && this.setState({visible: true}));
  };

  handleClose = () => {
    setDismissCookie(CLOSED_COOKIE, CLOSED_DAYS);
    this.setState({visible: false});
  };

  handleView = event => {
    setDismissCookie(INSTALLED_COOKIE, INSTALLED_DAYS);
    this.setState({visible: false});
    if (this.installPrompt) {
      event.preventDefault();
      this.installPrompt.prompt();
      this.installPrompt = null;
    }
  };

  render() {
    const {platform} = this.props;
    const store = STORE[platform];
    if (!this.state.visible || !store) {
      return null;
    }
    return (
      <Wrapper data-testid="app-banner" role="complementary">
        <CloseButton
          type="button"
          aria-label="Close"
          data-testid="app-banner-close"
          onClick={this.handleClose}
        >
          ×
        </CloseButton>
        <Icon src="/icon.png" alt="" />
        <Info>
          <Title>JJ Foodservice Ordering App</Title>
          <Author>JJ Foodservice Limited</Author>
          <Description>{store.description}</Description>
        </Info>
        <ViewButton
          href={store.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="app-banner-view"
          onClick={this.handleView}
        >
          View
        </ViewButton>
      </Wrapper>
    );
  }
}
